import * as Yup from 'yup'

import { type StopAppointment } from 'core/appointments/domain/Appointment'
import { type ShipmentContainer } from 'core/container-details/domain/ShipmentContainer'
import { type EquipmentSubtypes } from 'core/domain/Load'
import { type Mode } from 'core/domain/Mode'
import { type StopType } from 'core/domain/Stop'
import { type TruckType } from 'core/domain/Truck'
import { type Commodity } from 'core/shipping-items/domain/Commodity'
import { type PackageType } from 'core/shipping-items/domain/PackageType'
import {
  type ShippingItem,
  type ShippingItemsMeasures,
} from 'core/shipping-items/domain/ShippingItem'
import { isEmpty } from 'core/utils/array'
import { isFuture } from 'core/utils/date'

import {
  type OpenDockWarehouseFormFields,
  type OpenDockWarehouseFormFieldType,
} from '../infra/ShipmentMapper'
import { type Employee } from './Employee'
import { type Facility } from './Facility'
import { type Location } from './Location'

export type Item = {
  commodities: Array<Commodity>
  packageCount: string | number
  packageType?: PackageType
  weight: number | string
  width: string | number
  height: string | number
  length: string | number
  density?: number
  stackable: boolean
  turnable: boolean
  nmfcCode: string
  freightClass: string
  ltlExcessiveLengthClass: string
  hazmat: boolean
  hazmatClass: string
  unNumber: string
}

export type StopCheckType = 'in' | 'out'

export type Stop = {
  id: string | number
  stopType: StopType
  stopIndex: number
  date: string
  facility?: Facility
  city: string
  state: string
  zipcode: string
  country: string
  address?: string
  defaultContactUuid: string | null
  notes?: string
  blindBol?: boolean
  scaleTicket?: boolean
  lat?: string
  lng?: string
  contact?: {
    email?: string
    label?: string
    name?: string
    phoneNumber?: string
    value?: string
  }
  appointmentMode?: string
  checkInAt?: string
  checkOutAt?: string
  checkinTimeliness: string
  checkoutTimeliness: string
  isCheckinLate: boolean
  isCheckoutLate: boolean
  timezone: string
  appointment?: StopAppointment
}

export type Carrier = {
  dot?: string
  entityType?: 'asset' | 'broker'
  id: number
  mc?: string
  name: string
  uuid: string
  scac?: string
  email: string
}

export type EffectiveCarrier = {
  name: string
  dot?: string
  mc?: string
}

export type ShipperBusiness = 'BROKERAGE' | 'MTC' | 'SAAS' | 'FBM'

export type Shipper = {
  companyName: string
  logoEmailUrl: string
  email?: string
  business?: ShipperBusiness
}

export type Attachment = {
  id: number
  fileName: string
}

export type Accessorial = {
  code: string
  name: string
  stopType: Array<StopType>
}

export type PaymentTerms = 'prepaid' | 'collect' | 'third_party'

export type Shipment = {
  uuid: string
  accessorials: Array<Accessorial>
  shipperGuideId: string
  blindBol?: boolean
  spotQuote?: {
    uuid: string
    bookedRate?: {
      carrier: string
      flatRate: string
      ltlCarrier?: string
      uuid: string
      perMileRate?: string
    }
    attachments: Array<Attachment>
    notes?: string
  }
  mode: Mode
  equipmentType: TruckType
  equipmentSubType?: EquipmentSubtypes
  items: Array<ShippingItem>
  managedBy?: {
    contactName: string
    contactEmail: string
    contactPhone?: string
  }
  measures: ShippingItemsMeasures
  mileage: number
  shipperRefNumber: string
  hazmat: boolean
  soNumber?: string
  bolNumber?: string
  poNumbers?: string[]
  soNumbers?: string[]
  stops: Array<Stop>
  intermediaryStops?: Array<Stop>
  temperature?: number
  carrier: Carrier
  effectiveCarrier?: EffectiveCarrier
  shipper?: Shipper
  flatRate?: string
  isLoadsmartCarrier: boolean
  refNumber?: string | number
  status: string
  equipmentLength: string
  equipmentRequirements?: {
    temperature?: number | string
    temperatureUnit?: string
  } | null
  driver?: Employee
  transitEstimate: string
  tractorNumber?: string
  tarpType?: string
  tarpSize?: string
  appointment?: StopAppointment
  powerOnly: boolean
  trackingLink?: string
  proNumber?: string
  container?: ShipmentContainer
  podRequiredForAudit: boolean
  trailerNumber?: string
  sealNumber?: string
  cargoValue?: number
  paymentTerm?: PaymentTerms
  thirdPartyBillingInformation?: {
    companyName: string
    address: string
    zipcode: string
    city: string
    state: string
    country: string
  }
}

export type ShipmentTrackingStatus = 'not-started' | 'tracking' | 'not-tracking'

export function getShipmentTrackingStatus(
  shipment: Shipment,
  locations?: Location[]
): ShipmentTrackingStatus {
  if (!isEmpty(locations)) {
    return 'tracking'
  }

  const pickup = shipment.stops[0]
  const pickupAppt = pickup?.appointment

  const pickupDate = pickupAppt?.scheduledWindowStart
    ? new Date(pickupAppt.scheduledWindowStart)
    : // When there is no appointment, we use the stop date to determine if the tracking has not started.
      new Date(pickup.date)

  if (pickupDate && isFuture(pickupDate)) {
    return 'not-started'
  }

  return 'not-tracking'
}

export function getShipperBusiness(shipment?: Shipment): ShipperBusiness {
  return shipment?.shipper?.business ?? 'SAAS'
}

export function isFTL(shipment?: Shipment): boolean {
  return shipment?.mode === 'FTL'
}

export function isIMDL(shipment?: Shipment): boolean {
  return shipment?.mode === 'IMDL'
}

export function isEXP(shipment?: Shipment): boolean {
  return shipment?.mode === 'EXP'
}

export function isLTLorVLTL(shipment?: Shipment): boolean {
  if (!shipment) {
    return false
  }

  return shipment.mode === 'LTL' || shipment.mode === 'VLTL'
}

export function isDrayage(shipment?: Shipment): boolean {
  return shipment?.mode === 'DRAY' && shipment.container !== undefined
}

export function isOcean(shipment?: Shipment): boolean {
  return shipment?.mode === 'OCEAN' && shipment.container !== undefined
}

export function isBrokerCarrier(shipment?: Shipment) {
  return shipment?.carrier.entityType === 'broker'
}

export type OpenDockLoadTypes = {
  id: string
  name: string
  operation: string
  allowCarrierScheduling: boolean
  allowCarrierDockSelection: boolean
  direction: string
  docks: Array<{
    id: string
    name: string
    sortOrder: number
  }>
  duration: number
  equipmentType: string
  mode: string
}

export type OpenDockLoadTypeSummary = Omit<
  OpenDockLoadTypes,
  'allowCarrierScheduling' | 'allowCarrierDockSelection' | 'docks'
>

export type DockAvailability = {
  dockId: string
  sortOrder: number
  startTimes: Array<string>
}

export type OpenDockWarehouseDetails = {
  uuid: string
  name: string
  ppeRequirements: Array<string>
  yardMapImg: string
  allowCarrierScheduling: boolean
  amenities: Array<string>
  instructions: string
  loadTypes: Array<OpenDockLoadTypes>
}

export type OpenDockStopAppointment = {
  id: string
  status: string
  start: string
  end: string
  loadType: OpenDockLoadTypeSummary
  dockId: string
  dockName: string
  confirmationNumber: string
  notes: string
}

function getValidationSchemaPerFieldType(
  fieldType: OpenDockWarehouseFormFieldType,
  fieldName: string,
  required: boolean
) {
  const schema: { [key: string]: any } = {}

  switch (fieldType) {
    case 'str':
      schema[fieldName] = required ? Yup.string().required('Required') : Yup.string().nullable()
      break
    case 'int':
      schema[fieldName] = required ? Yup.number().required('Required') : Yup.number().nullable()
      break
    case 'phone':
      schema[fieldName] = required ? Yup.string().required('Required') : Yup.string().nullable()
      break
    case 'email':
      schema[fieldName] = required
        ? Yup.string().email('Invalid email').required('Required')
        : Yup.string().email('Invalid email').nullable()
      break
    case 'bigstr':
      schema[fieldName] = required
        ? Yup.string().max(1000, 'Must be 1000 characters or less').required('Required')
        : Yup.string().max(1000, 'Must be 1000 characters or less').nullable()
      break
    case 'bool':
      schema[fieldName] = required ? Yup.boolean().required('Required') : Yup.boolean().nullable()
      break
    case 'date':
      schema[fieldName] = required ? Yup.date().required('Required') : Yup.date().nullable()
      break
    case 'dropdown':
    case 'combobox':
      schema[fieldName] = required ? Yup.object().required('Required') : Yup.object().nullable()
      break
    case 'dropdownmultiselect':
      schema[fieldName] = required ? Yup.array().required('Required') : Yup.array().nullable()
      break
    default:
      break
  }

  return schema
}

export function buildValidationSchema(fields: OpenDockWarehouseFormFields) {
  const schema = {}

  fields.formFields.forEach((field, index) => {
    Object.assign(
      schema,
      getValidationSchemaPerFieldType(field.type, `form-fields-${index}`, field.required)
    )
  })

  fields.customFormFields.forEach((field, index) => {
    Object.assign(
      schema,
      getValidationSchemaPerFieldType(field.type, `custom-form-fields-${index}`, field.required)
    )
  })

  if (fields.refNumbers) {
    Object.assign(
      schema,
      getValidationSchemaPerFieldType('str', 'refNumbers', fields.refNumbers.required)
    )
  }

  return Yup.object().shape(schema)
}

export function getDockWithLowestSortOrder(docks: OpenDockLoadTypes['docks']) {
  return docks.reduce((prev, curr) => {
    return curr.sortOrder < prev.sortOrder ? curr : prev
  }, docks[0])
}
